// Historie změn u úkolů — převod syrového řádku z DB na lidskou větu.
//
// Backend ukládá jen holá data (pole, hodnota_z, hodnota_na, kdo, kdy).
// Formulace se liší podle pole, ať se to čte jako věta a ne jako diff:
//   deadline → posunul / nastavil / zrušil termín
//   pilot    → přeřadil na / přiřadil / odebral
//   status   → přesunul do
//   priority → změnil prioritu
//   nazev    → přejmenoval
//
// Sdílí Operations board, trenérský board i stránka Historie úkolů, ať je
// znění všude stejné.

// Popisky sloupců obou kanbanů (company i trenéři) v jedné mapě — historie
// míchá oba boardy a stav se ukládá jako klíč, ne jako popisek.
export const STATUS_POPISKY = {
  zasobnik: "Zásobník",
  prioritni: "Prioritní",
  mesicni: "Měsíční",
  tydenni: "Týdenní",
  v_procesu: "V procesu",
  naplanovane: "Naplánované",
  ke_kontrole: "Ke kontrole",
  hotovo: "Hotovo",
  archiv: "Archiv",
};

export const POLE_POPISKY = {
  deadline: "Termín",
  pilot: "Pilot",
  status: "Sloupec",
  priority: "Priorita",
  nazev: "Název",
  podukol: "Podúkol",
};

const PRIORITA_POPISKY = { High: "Vysoká", Medium: "Střední", Low: "Nízká", TOP: "TOP" };

const statusLabel = (v) => STATUS_POPISKY[String(v || "")] || String(v || "");
const prioritaLabel = (v) => PRIORITA_POPISKY[String(v || "")] || String(v || "");

// "2026-08-14" → "14. 8." (rok jen když je jiný než letošní, ať to není ukecané).
export function datumKratce(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso || "");
  const rok = Number(m[1]);
  const zaklad = `${Number(m[3])}. ${Number(m[2])}.`;
  return rok === new Date().getFullYear() ? zaklad : `${zaklad} ${rok}`;
}

// ISO timestamp → "7. 8. v 9:12"
export function kdyKratce(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const den = `${d.getDate()}. ${d.getMonth() + 1}.`;
  const cas = `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
  const rok = d.getFullYear() === new Date().getFullYear() ? "" : ` ${d.getFullYear()}`;
  return `${den}${rok} v ${cas}`;
}

// Samotná věta bez autora a času, např. "posunul deadline z 10. 8. na 14. 8.".
export function vetaZmeny(radek) {
  const pole = String(radek?.pole || "");
  const z = String(radek?.hodnota_z || "");
  const na = String(radek?.hodnota_na || "");

  if (pole === "deadline") {
    if (z && na) return `posunul deadline z ${datumKratce(z)} na ${datumKratce(na)}`;
    if (na) return `nastavil deadline na ${datumKratce(na)}`;
    if (z) return `zrušil deadline (byl ${datumKratce(z)})`;
    return "změnil deadline";
  }
  if (pole === "pilot") {
    if (z && na) return `přeřadil úkol z ${z} na ${na}`;
    if (na) return `přeřadil úkol na ${na}`;
    if (z) return `odebral pilota (byl ${z})`;
    return "změnil pilota";
  }
  if (pole === "status") {
    if (z && na) return `přesunul úkol z ${statusLabel(z)} do ${statusLabel(na)}`;
    if (na) return `přesunul úkol do ${statusLabel(na)}`;
    return "změnil sloupec";
  }
  if (pole === "priority") {
    if (z && na) return `změnil prioritu z ${prioritaLabel(z)} na ${prioritaLabel(na)}`;
    if (na) return `nastavil prioritu ${prioritaLabel(na)}`;
    if (z) return `zrušil prioritu (byla ${prioritaLabel(z)})`;
    return "změnil prioritu";
  }
  if (pole === "nazev") {
    if (z && na) return `přejmenoval úkol z „${z}" na „${na}"`;
    if (na) return `pojmenoval úkol „${na}"`;
    return "přejmenoval úkol";
  }
  // Podúkoly (24.8.2026): přidání má prázdné „z", smazání prázdné „na".
  // Odškrtávání se do historie schválně nezapisuje.
  if (pole === "podukol") {
    if (z && na) return `přepsal podúkol „${z}" na „${na}"`;
    if (na) return `přidal podúkol „${na}"`;
    if (z) return `smazal podúkol „${z}"`;
    return "změnil podúkoly";
  }
  // Nové sledované pole, které tenhle frontend ještě nezná — ať se aspoň něco zobrazí.
  if (z && na) return `změnil ${POLE_POPISKY[pole] || pole} z ${z} na ${na}`;
  return `změnil ${POLE_POPISKY[pole] || pole}`;
}

// Celý řádek: "Michal Šulc posunul deadline z 10. 8. na 14. 8. — 7. 8. v 9:12"
export function popisZmeny(radek) {
  const kdo = String(radek?.kdo || "").trim() || "Někdo";
  const kdy = kdyKratce(radek?.kdy);
  return `${kdo} ${vetaZmeny(radek)}${kdy ? `, ${kdy}` : ""}`;
}

// Barva odznáčku podle pole — deadline vizuálně vyskočí, o ten jde nejčastěji.
export const POLE_TON = {
  deadline: "bg-amber-50 text-amber-700 border-amber-200",
  pilot: "bg-sky-50 text-sky-700 border-sky-200",
  status: "bg-plum-50 text-plum-700 border-plum-200",
  priority: "bg-rose-50 text-rose-700 border-rose-200",
  nazev: "bg-slate-100 text-slate-700 border-slate-200",
  podukol: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
export const poleTon = (pole) => POLE_TON[String(pole || "")] || "bg-slate-100 text-slate-600 border-slate-200";
